import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { IconAlert, IconCheck, IconClose } from './icons.tsx'

type Kind = 'ok' | 'error'
type Item = { id: number; kind: Kind; title: string; body?: string }

const Ctx = createContext<{
  ok: (title: string, body?: string) => void
  fail: (title: string, body?: string) => void
} | null>(null)

let seq = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([])

  const dismiss = useCallback((id: number) => {
    setItems(list => list.filter(t => t.id !== id))
  }, [])

  const push = useCallback((kind: Kind, title: string, body?: string) => {
    const id = ++seq
    setItems(list => [...list, { id, kind, title, body }])
    // Errors stay longer: a failure is something to read and act on, a
    // success only needs acknowledging.
    window.setTimeout(() => dismiss(id), kind === 'error' ? 7000 : 3500)
  }, [dismiss])

  const value = useMemo(() => ({
    ok: (t: string, b?: string) => push('ok', t, b),
    fail: (t: string, b?: string) => push('error', t, b),
  }), [push])

  return (
    <Ctx.Provider value={value}>
      {children}
      {/* Announced to screen readers without stealing focus from the form
          the person is still working in. */}
      <div className="toasts" role="status" aria-live="polite">
        {items.map(t => (
          <div key={t.id} className={`toast ${t.kind}`}>
            <span style={{ color: t.kind === 'ok' ? 'var(--ok)' : 'var(--danger)', marginTop: 1 }}>
              {t.kind === 'ok' ? <IconCheck size={15} /> : <IconAlert size={15} />}
            </span>
            <div className="grow">
              <div className="t-title">{t.title}</div>
              {t.body ? <div className="t-body">{t.body}</div> : null}
            </div>
            <button className="btn ghost sm" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              <IconClose size={14} />
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  )
}

export function useToast() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
